import { Button, Grid, LinearProgress } from "@mui/material";
import imageCompression from "browser-image-compression";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { fetchAIChat, fetchBillReader } from "../pages/service/fetch";
import {
  BillWrapper,
  CustomLinearProgress,
  FileItem,
  Remind,
  ShowFile,
  StepConnector,
  StepHeader,
  TableList,
  Tips,
  UploadButton,
} from "./styles/common.styled";
import { ViewPdf, getTableData } from "./Comps";
import { TypingComp } from "./Typing";
import { AddrData, AddrType } from "./types";
import React from "react";
import { useNavigate } from "react-router-dom";

const progressLabels = [
  "Chunking the PDF into charts, tables, and text blocks",
  "Understanding text from blocks",
  "Scanning bill for all meters with usage data",
  "Extracting usage data from meters",
  "Formatting data into readable blocks",
  "Summarizing all figures into target language",
  "Extracting all tables into target language",
  "Preparing results for display",
];

export const BillReader = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [billData, setBillData] = useState<any>("");
  const [isFetching, setIsFetching] = useState(false);
  const [progress, setProgress] = useState<number[]>(Array(8).fill(0));
  const [showBill, setShowBill] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [step, setStep] = useState(1);
  const [index, setIndex] = useState(0);
  const [aiText, setAIText] = useState("");
  const mainRef = useRef<null | HTMLDivElement>(null);
  const [addrData, setAddrData] = useState<AddrData>({
    address: [],
    meterId: "",
    addr: [],
    esi_id: "",
    account_number:"",
  });
  const navigate = useNavigate();
  
  const handleDelete = () => {
    files.forEach((file) => URL.revokeObjectURL((file as any).preview));
    setFiles([]);
  };

  useEffect(() => {
    let file: any = files[0] || {};
    if (file.type === "application/pdf") {
      setIsPdf(true);
    } else {
      setIsPdf(false);
    }
  }, [files]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      console.log('isFetching:', isFetching);
      if (isFetching) return;

      try {
        let processedFile = acceptedFiles[0];

        if (["image/png", "image/jpeg"].includes(processedFile.type)) {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          processedFile = await imageCompression(processedFile, options);
        }
        setFiles([
          Object.assign(processedFile, {
            preview: URL.createObjectURL(processedFile),
          }),
        ]);

        if (showBill) {
          setAIText("");
          setBillData("");
          setStep(1)
          handleStartDemo(processedFile);
        }
      } catch (error) {
        console.error("Error processing file:", error);
      }
    },
    [files.length, showBill, isFetching]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    // maxFiles: 1,
    // noClick: files.length > 0,
    // noKeyboard: files.length > 0,
  });

  useEffect(() => {
    billData && setAddrData(getTableData(billData));
  }, [billData]);

  const getAIData = (data: any) => {
    sessionStorage.removeItem("typed");
    fetchAIChat(JSON.stringify(data)).then((res: any) => {
      if (res && res.status === 1) {
        setAIText(res.response.answer);
        setIsFetching(false);
      } else {
        setIndex(1);
        setIsFetching(false);
      }
      setProgress(Array(8).fill(100));
    });
  };

  const handleStartDemo = async (file: any) => {
    if (files.length === 0 || isFetching) return;
    setShowBill(true);

    setIsFetching(true);
    setProgress(Array(8).fill(0));

    const apiCall = fetchBillReader(file);
    const progressSimulation = simulateProgress();

    try {
      const [response] = await Promise.all([apiCall, progressSimulation]);
      if (response) {
        setBillData(response);
        getAIData(response);
      }
    } catch (error) {
      console.error("Error processing bill:", error);
    }
  };

  const simulateProgress = async () => {
    const localProgress = Array(8).fill(0);

    for (let i = 0; i < 8; i++) {
      await new Promise((resolve) =>
        setTimeout(resolve, 100 + Math.random() * 500)
      );

      while (localProgress[i] < 100) {
        const increment = 5 + Math.floor(Math.random() * 20);
        const delay = 100 + Math.random() * 500;

        await new Promise((resolve) => setTimeout(resolve, delay));

        localProgress[i] = Math.min(100, localProgress[i] + increment);

        setProgress([...localProgress]);
      }
    }
  };

  const thumbs = files.map((file: any) => {
    return (
      <ShowFile key={file.name}>
        {isPdf && <iframe src={file.preview} title={file.name} />}

        {!isPdf && <img src={file.preview} alt={file.name} />}
      </ShowFile>
    );
  });

  const renderStepHeader = () => (
    <StepHeader>
      <h3 className={step === 1 ? "active" : ""}>✨ AI ANALYSIS (BETA)</h3>
      <StepConnector />
      <h3 className={step === 2 ? "active" : ""}>TABLE</h3>
      <StepConnector />
      <h3 className={step === 3 ? "active" : ""}>JSON</h3>
    </StepHeader>
  );
  console.log(files)

  const renderStepControls = () => (
    <div
      style={{
        marginTop: "auto",
        display: "flex",
        gap: "10px",
        paddingTop: 20,
      }}
    >
      {step > 1 && (
        <Button
          variant="outlined"
          onClick={() => setStep((s) => s - 1)}
          style={{ width: "50%" }}
        >
          Back
        </Button>
      )}

      {step < 3 ? (
        <Button
          variant="contained"
          onClick={() => setStep((s) => s + 1)}
          style={{ width: step === 1 ? "100%" : "50%" }}
        >
          Next Step
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={() => navigate('/contact')}
          style={{width:"50%"}}
        >
          Contact Us
        </Button>
      )}
    </div>
  );

  const uploadAnotherButton = (
    <div {...getRootProps()} className="drop">
      <input {...getInputProps()} />
      <Button
        variant="outlined"
        style={{
          marginTop: "auto",
          display: "block",
          minHeight: "35px",
          borderRadius: "5px",
          textTransform: "unset",
          width: "100%",
        }}
      >
        Upload another bill
      </Button>
    </div>
  );

  const renderAddress = () => {
    if (!addrData.address.length) return <p>-</p>;
    if (addrData.address.length === 1) return <p>{addrData.address[0]}</p>;

    return addrData.address.map((a, index) => (
      <p key={a}>
        Address {index + 1} : {a}
      </p>
    ));
  };

  const getAddrProperty = (prop: keyof AddrType) => {
    return addrData.addr[0]?.[prop] || "-";
  };

  const renderExtensionInfo = () => (
    <>
      {billData?.ext?.company && (
        <TableList>
          <strong>Company</strong>
          <p>{billData.ext.company}</p>
        </TableList>
      )}
      {billData?.ext?.puct && (
        <TableList>
          <strong>PUCT</strong>
          <p>{billData.ext.puct}</p>
        </TableList>
      )}
    </>
  );

  const AIAnalysis = () => (
      <TypingComp
        className="ai-analysis"
        text={aiText}
        mainRef={mainRef}
        type="pc"
      />
  );
  const TableView = ({ data }: any) => (
    <div>
      <TableList>
        <strong>Account Number</strong>
        <p>{addrData.account_number || " - "}</p>
      </TableList>
      <TableList>
        <strong>Meter ID</strong>
        <p>{addrData.meterId || " - "}</p>
      </TableList>
      <TableList>
        <strong>ESIID</strong>
        <p>{addrData.esi_id || " - "}</p>
      </TableList>
      <TableList>
        <strong>Address</strong>
        {renderAddress()}
      </TableList>
      <TableList>
        <strong>Street</strong>
        <p>{getAddrProperty("road")}</p>
      </TableList>
      <TableList>
        <strong>City</strong>
        <p>{getAddrProperty("city")}</p>
      </TableList>
      <TableList>
        <strong>Zipcode</strong>
        <p>{getAddrProperty("postcode")}</p>
      </TableList>
      {renderExtensionInfo()}
    </div>
  );
  const JsonView = ({ data }: any) => (
    <div>
      <pre
        style={{
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );

  useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL((file as any).preview));
    };
  }, [files]);

  return (
    <BillWrapper>
      <Grid container>
        {!showBill && (
          <Grid
            item
            sm={6}
            style={{
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #E4E4E4",
              minHeight: "780px",
            }}
          >
            <h5>Choose your bill</h5>

            <div {...getRootProps()} className="drop">
              <input {...getInputProps()} />
              <UploadButton>
                <img src={require("../pages/images/icon-upload.png")} alt="" />
                <p>Upload an electricity bill</p>
                <label>
                  Click to upload file(s). Wait for all files to upload before
                  submitting.
                </label>
              </UploadButton>
            </div>

            <aside>
              {files.map((file) => (
                <FileItem key={file.name}>
                  <span>{file.name}</span>
                  <button onClick={handleDelete}>×</button>
                </FileItem>
              ))}
            </aside>

            <Button
              onClick={()=> handleStartDemo(files[0])}
              disabled={files.length === 0 || isFetching}
              variant="contained"
              style={{
                marginTop: "auto",
                display: "block",
                minHeight: "35px",
                borderRadius: "5px",
                textTransform: "unset",
              }}
            >
              Start demo
            </Button>
          </Grid>
        )}
        {showBill && (
          <Grid
            item
            sm={6}
            style={{
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #E4E4E4",
              minHeight: "780px",
            }}
          >
            {thumbs}
            {uploadAnotherButton}
          </Grid>
        )}

        <Grid
          item
          sm={6}
          style={{
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            minHeight: "780px",
          }}
        >
          <Remind>
            Try the utility bill parsing system. This live demo only works on
            utility invoices. The file size and page count is restricted to
            prevent abuse of the demo.
          </Remind>

          {!billData && !isFetching &&(
            <Tips>
              <p>File Upload Requirements for the demo:</p>
              <span>PDF and images only</span>
              <span>Fewer than 10pages and 15MB</span>
            </Tips>
          )}

          {isFetching && (
            <div>
              {progress.map((value, index) => (
                <div key={index} style={{ margin: "16px 0" }}>
                  <CustomLinearProgress
                    variant="determinate"
                    value={value}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <p style={{ fontSize: 14, margin: "8px 0" }}>
                    {progressLabels[index]}
                  </p>
                </div>
              ))}
            </div>
          )}
          {billData && aiText && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {renderStepHeader()}

              <div className="bill-reader-tabs" style={{ flex: 1, overflowY: "scroll", maxHeight: "545px"}} ref={mainRef}>
                {step === 1 && <AIAnalysis/>}
                {step === 2 && <TableView data={billData} />}
                {step === 3 && <JsonView data={billData} />}
              </div>

              {renderStepControls()}
            </div>
          )}
        </Grid>
      </Grid>
    </BillWrapper>
  );
};
